import React, { useCallback, useMemo } from "react"

import type { MetricIdentity } from "@/metrics/catalog"
import { TableColumnToggle, type ColumnDef } from "@coval/ui"

import {
  applyMetricOrder,
  orderMetricIds,
  type MetricViews,
} from "@/types/MetricViewConfig"
import { resolveMetricDisplayName } from "@/utils/formatting/format-metric-display-results"

interface MetricViewsSectionProps {
  /** All valid metric IDs present in the run data. */
  metricIds: string[]
  metricRecordsById: Record<string, MetricIdentity>
  metricViews: MetricViews
  onMetricViewsChange: (metricViews: MetricViews) => void
}

/**
 * The report's metric chips, without any surrounding popover chrome. A click
 * shows or hides a metric; the grip drags it to a new position, which the
 * report's table and metric cards follow.
 */
export const MetricViewsSection: React.FC<MetricViewsSectionProps> = ({
  metricIds,
  metricRecordsById,
  metricViews,
  onMetricViewsChange,
}) => {
  const columns = useMemo<ColumnDef<unknown>[]>(
    () =>
      metricIds.map((metricId) => {
        const record = metricRecordsById[metricId]
        return {
          id: metricId,
          header: resolveMetricDisplayName(
            record?.metric_display_name,
            record?.metric_name || metricId,
          ),
        }
      }),
    [metricIds, metricRecordsById],
  )

  const columnOrder = useMemo(
    () => orderMetricIds(metricIds, metricViews),
    [metricIds, metricViews],
  )

  const columnVisibility = useMemo(
    () =>
      Object.fromEntries(
        metricIds.map((metricId) => [
          metricId,
          metricViews[metricId]?.visible ?? true,
        ]),
      ),
    [metricIds, metricViews],
  )

  const handleVisibilityChange = useCallback(
    (visibility: Record<string, boolean>) => {
      const next: MetricViews = { ...metricViews }
      for (const [metricId, visible] of Object.entries(visibility)) {
        if ((metricViews[metricId]?.visible ?? true) !== visible) {
          next[metricId] = { ...metricViews[metricId], visible }
        }
      }
      onMetricViewsChange(next)
    },
    [metricViews, onMetricViewsChange],
  )

  const handleOrderChange = useCallback(
    (order: string[]) => {
      onMetricViewsChange(applyMetricOrder(metricViews, order))
    },
    [metricViews, onMetricViewsChange],
  )

  return (
    <div className="px-3 pb-2">
      <TableColumnToggle
        inline
        columns={columns}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={handleVisibilityChange}
        minVisible={0}
        columnOrder={columnOrder}
        onColumnOrderChange={handleOrderChange}
        searchable={metricIds.length > 8}
        searchPlaceholder="Search metrics…"
      />
    </div>
  )
}
